import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Lenis from 'lenis';
import { CursorProvider } from './context/CursorContext';
import { CinematicPreloader } from './components/Preloader';
import { BestofLayout } from './components/layout/BestofLayout';
import { HomePage } from './components/home/HomePage';
import { ProjectPage } from './components/project/ProjectPage';
import { ContactPage } from './components/contact/ContactPage';
import { WorkPage } from './components/work/WorkPage';
import { ExperiencePage } from './components/experience/ExperiencePage';
import { AboutPage } from './components/about/AboutPage';
import { ImpressumPage } from './components/legal/ImpressumPage';
import { LievyPrivacyPage } from './components/legal/LievyPrivacyPage';
import { LievyTermsPage } from './components/legal/LievyTermsPage';
import { NotFoundPage } from './components/404/NotFoundPage';

// ═══════════════════════════════════════════════════════════════════
// BESTOF VERSION - THE ULTIMATE PORTFOLIO
// Combines the best of Classic + Awwwards:
// - Custom cursor & preloader from Awwwards
// - Horizontal scroll projects gallery from Awwwards
// - Skills grid from Classic (in Awwwards style)
// - Experience timeline from Classic (in Awwwards style)
// - Noise overlay & smooth scroll from Awwwards
// - React Router for project pages and contact
// ═══════════════════════════════════════════════════════════════════

function BestofAppContent() {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  // Initialize Lenis smooth scrolling
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const handlePreloaderComplete = () => {
    setIsLoading(false);
    // Small delay before marking as fully loaded for staggered animations
    setTimeout(() => setIsLoaded(true), 100);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white selection:bg-emerald-500/30 selection:text-white cursor-none">
      {/* Cinematic Preloader */}
      {isLoading && <CinematicPreloader onComplete={handlePreloaderComplete} />}

      {/* Router */}
      <Routes>
        <Route element={<BestofLayout isLoaded={isLoaded} />}>
          <Route path="/" element={<HomePage isLoaded={isLoaded} />} />
          <Route path="/work" element={<WorkPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/experience" element={<ExperiencePage />} />
          <Route path="/project/:projectId" element={<ProjectPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/lievy/privacy" element={<LievyPrivacyPage />} />
          <Route path="/lievy/terms" element={<LievyTermsPage />} />
          <Route path="/privacy" element={<LievyPrivacyPage />} />
          <Route path="/terms" element={<LievyTermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </div>
  );
}

export function BestofApp() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <CursorProvider>
          <BestofAppContent />
        </CursorProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default BestofApp;
